import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useWedding } from '../contexts/WeddingContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Group, Wallet, GraphUp, CheckCircle, WarningTriangle, Trash, RefreshDouble,
  Search, Sparks, Refresh, Activity, Plus, BookStack, Flower, OpenInBrowser,
  SendMail, Clock, Xmark,
} from 'iconoir-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ───────────── OVERVIEW ─────────────
function StatCard({ label, value, sublabel, accent = "charcoal" }) {
  const accentClass = { terracotta: "text-terracotta", moss: "text-moss", ochre: "text-ochre", charcoal: "text-charcoal" }[accent];
  return (
    <Card className="card-surface p-6">
      <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-2">{label}</p>
      <p className={`font-serif font-light text-4xl ${accentClass}`}>{value}</p>
      {sublabel && <p className="text-xs text-muted mt-2 font-light">{sublabel}</p>}
    </Card>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/funnel?hours=168`, { withCredentials: true }),
      ]);
      setStats(s.data);
      setFunnel(f.data);
    } catch (err) {
      toast.error('Failed to load stats');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !stats) return <p className="text-muted text-sm">Loading metrics…</p>;

  const topCount = funnel?.steps?.[0]?.count ?? 0;

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-charcoal">At a glance</h2>
        <Button onClick={load} className="btn-secondary text-xs gap-2" data-testid="admin-refresh-btn">
          <Refresh className="w-3.5 h-3.5" /> Refresh
        </Button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total users"
          value={stats?.total_users ?? 0}
          sublabel={`+${stats?.users_24h ?? 0} last 24h · +${stats?.users_7d ?? 0} last 7d`}
        />
        <StatCard
          label="Paid customers"
          value={stats?.paid_users ?? 0}
          sublabel={`${stats?.paid_transactions ?? 0} transactions`}
          accent="moss"
        />
        <StatCard
          label="Revenue"
          value={fmtCurrency(stats?.revenue_usd)}
          sublabel={stats?.refunded_transactions > 0 ? `${stats.refunded_transactions} refunded` : 'All-time, real sales only'}
          accent="ochre"
        />
        <StatCard
          label="Current tier"
          value={fmtCurrency(stats?.current_tier_price)}
          sublabel={`${stats?.slots_claimed_in_tier ?? 0} / ${stats?.slots_total ?? 100} slots claimed`}
          accent="terracotta"
        />
      </div>

      {/* Funnel */}
      <Card className="card-surface p-6" data-testid="admin-funnel">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl text-charcoal">Conversion funnel (last 7 days)</h3>
          <span className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted">Landing → Paid</span>
        </div>
        <div className="space-y-4">
          {funnel?.steps?.map((step, i) => {
            const maxWidth = Math.max(topCount, 1);
            const widthPct = Math.max(3, (step.count / maxWidth) * 100);
            const color = ["bg-terracotta", "bg-ochre", "bg-moss", "bg-charcoal"][i] || "bg-charcoal";
            return (
              <div key={step.key} data-testid={`funnel-step-${step.key}`}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-sm font-medium text-charcoal">{step.label}</span>
                  <span className="text-xs font-mono text-muted">
                    <span className="text-charcoal font-medium">{step.count.toLocaleString()}</span>
                    {i > 0 && <span className="ml-3">{step.conversion_from_top}% of top</span>}
                  </span>
                </div>
                <div className="h-7 bg-page-alt rounded-sm overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-sm transition-all duration-700`}
                    style={{ width: `${widthPct}%`, opacity: 0.85 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted mt-6 font-light">
          Landing views and modal opens are tracked client-side; checkouts & paid come from Stripe transactions.
        </p>
      </Card>
    </div>
  );
}

// ───────────── PENDING INVITES ─────────────
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const diffMs = new Date(iso).getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const mins = Math.round(absMs / 60000);
  const hours = Math.round(absMs / 3600000);
  const days = Math.round(absMs / 86400000);
  const tense = diffMs >= 0 ? 'in ' : '';
  const suffix = diffMs >= 0 ? '' : ' ago';
  if (mins < 60) return `${tense}${mins}m${suffix}`;
  if (hours < 24) return `${tense}${hours}h${suffix}`;
  return `${tense}${days}d${suffix}`;
};

function PendingInvitesCard({ refreshKey = 0, onChange }) {
  const [invites, setInvites] = useState([]);
  const [totals, setTotals] = useState({ pending: 0, redeemed: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/invites?status=pending&limit=100`, { withCredentials: true });
      setInvites(data.items || []);
      setTotals(data.totals || { pending: 0, redeemed: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load invites');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleResend = async (inv) => {
    setBusyId(inv.id);
    try {
      await axios.post(`${API}/admin/users/invites/${inv.id}/resend`, {}, { withCredentials: true });
      toast.success(`Invite resent to ${inv.email}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Resend failed');
    } finally { setBusyId(null); }
  };

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    const inv = confirmRevoke;
    setBusyId(inv.id);
    try {
      await axios.delete(`${API}/admin/users/invites/${inv.id}`, { withCredentials: true });
      toast.success(`Invite to ${inv.email} revoked`);
      setConfirmRevoke(null);
      await load();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    } finally { setBusyId(null); }
  };

  // onChange is kept for future hooks; nothing else reloads here
  void onChange;

  return (
    <Card className="card-surface p-5" data-testid="admin-pending-invites-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-ochre/10 flex items-center justify-center shrink-0">
            <SendMail className="w-5 h-5 text-ochre" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-serif text-lg text-charcoal leading-tight">Pending invites</h3>
            <p className="text-xs text-muted font-mono uppercase tracking-wider mt-1" data-testid="admin-pending-invites-count">
              {totals.pending} unredeemed · {totals.redeemed} redeemed
            </p>
          </div>
        </div>
        <Button variant="ghost" onClick={load} disabled={loading} className="gap-2 text-muted hover:text-charcoal" data-testid="admin-pending-invites-refresh">
          <Refresh className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
          Refresh
        </Button>
      </div>

      {loading && invites.length === 0 ? (
        <p className="text-sm text-muted py-6 text-center">Loading…</p>
      ) : invites.length === 0 ? (
        <p className="text-sm text-muted py-6 text-center" data-testid="admin-pending-invites-empty">
          No pending invites. Send one via <span className="font-mono">New user → Send invite email</span>.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-5">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invitee</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Resends</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invites.map((inv) => (
                <TableRow key={inv.id} data-testid={`invite-row-${inv.id}`}>
                  <TableCell>
                    <div className="text-sm text-charcoal font-mono">{inv.email}</div>
                    <div className="text-xs text-muted mt-0.5">{inv.name || '—'} · <span className="uppercase tracking-wider">{inv.role}</span></div>
                  </TableCell>
                  <TableCell className="text-xs text-muted">
                    {fmtRelative(inv.sent_at)}
                  </TableCell>
                  <TableCell>
                    {inv.expired ? (
                      <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-1 rounded-sm" data-testid={`invite-expired-${inv.id}`}>
                        <Clock className="w-3 h-3" strokeWidth={1.75} /> Expired
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-moss">
                        <Clock className="w-3 h-3" strokeWidth={1.75} /> {fmtRelative(inv.expires_at)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted font-mono">
                    {inv.resend_count > 0 ? `${inv.resend_count}×` : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResend(inv)}
                        disabled={busyId === inv.id}
                        className="gap-1.5 text-xs"
                        data-testid={`invite-resend-btn-${inv.id}`}
                      >
                        <SendMail className="w-3.5 h-3.5" strokeWidth={1.75} />
                        {busyId === inv.id ? 'Sending…' : 'Resend'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmRevoke(inv)}
                        disabled={busyId === inv.id}
                        className="gap-1.5 text-xs text-red-700 hover:bg-red-50 hover:border-red-300"
                        data-testid={`invite-revoke-btn-${inv.id}`}
                      >
                        <Xmark className="w-3.5 h-3.5" strokeWidth={2} />
                        Revoke
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <DialogContent data-testid="admin-revoke-invite-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Revoke this invite?</DialogTitle>
            <DialogDescription>
              The invite link sent to <span className="font-mono text-charcoal">{confirmRevoke?.email}</span> will stop working immediately, even if the recipient hasn't opened it yet. They can be re-invited later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
            <Button onClick={handleRevoke} className="bg-red-600 text-white hover:bg-red-700" data-testid="admin-revoke-invite-confirm-btn">
              Revoke invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ───────────── USERS ─────────────
function UsersTab() {
  const navigate = useNavigate();
  const { weddings, setCurrentWedding, fetchWeddings } = useWedding();
  const demoWedding = (weddings || []).find(w => w.is_demo);
  const [demoBusy, setDemoBusy] = useState(null); // 'create' | 'open' | 'delete' | null
  const [confirmDeleteDemo, setConfirmDeleteDemo] = useState(false);
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);

  const handleCreateDemo = async () => {
    setDemoBusy('create');
    try {
      const { data } = await axios.post(`${API}/admin/demo-wedding`, {}, { withCredentials: true });
      await fetchWeddings();
      if (data?.wedding) setCurrentWedding(data.wedding);
      toast.success(data?.created ? 'Demo wedding created — opening dashboard' : 'Demo wedding already exists — opening it');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create demo wedding');
    } finally { setDemoBusy(null); }
  };

  const handleOpenDemo = () => {
    if (!demoWedding) return;
    setCurrentWedding(demoWedding);
    navigate('/dashboard');
  };

  const handleDeleteDemo = async () => {
    setDemoBusy('delete');
    try {
      await axios.delete(`${API}/admin/demo-wedding`, { withCredentials: true });
      await fetchWeddings();
      toast.success('Demo wedding deleted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete demo wedding');
    } finally {
      setDemoBusy(null);
      setConfirmDeleteDemo(false);
    }
  };

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, user }
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState('password'); // 'password' | 'invite'
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'user', is_paid: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (q) params.set('q', q);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setUsers(data.users);
      setTotal(data.total);
    } catch (err) {
      toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const runAction = async () => {
    if (!confirmAction) return;
    const { type, user } = confirmAction;
    try {
      if (type === 'grant') {
        await axios.post(`${API}/admin/users/${user.id}/grant-access`, {}, { withCredentials: true });
        toast.success(`Granted access to ${user.email}`);
      } else if (type === 'revoke') {
        await axios.post(`${API}/admin/users/${user.id}/revoke-access`, {}, { withCredentials: true });
        toast.success(`Revoked access for ${user.email}`);
      } else if (type === 'delete') {
        await axios.delete(`${API}/admin/users/${user.id}`, { withCredentials: true });
        toast.success(`Deleted ${user.email}`);
      }
      setConfirmAction(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      if (createMode === 'invite') {
        await axios.post(`${API}/admin/users/invite`, {
          email: newUser.email, name: newUser.name, role: newUser.role, is_paid: newUser.is_paid,
        }, { withCredentials: true });
        toast.success(`Invite sent to ${newUser.email}`);
        setInvitesRefreshKey(k => k + 1);
      } else {
        await axios.post(`${API}/admin/users`, newUser, { withCredentials: true });
        toast.success(`Created ${newUser.email}`);
      }
      setCreateOpen(false);
      setNewUser({ email: '', name: '', password: '', role: 'user', is_paid: true });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      {/* Demo Wedding quick-access card */}
      <Card className="card-surface p-5" data-testid="admin-demo-wedding-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-terracotta/10 flex items-center justify-center shrink-0">
              <Flower className="w-5 h-5 text-terracotta" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h3 className="font-serif text-lg text-charcoal leading-tight">Demo wedding</h3>
              <p className="text-sm text-muted mt-1">
                {demoWedding
                  ? <>A fully-populated sample wedding is ready — <span className="font-mono text-charcoal">{demoWedding.name}</span>. Open it to explore every feature without entering real data.</>
                  : <>Spin up a pre-populated sample wedding (events, checklist, vendors, expenses, guests, seating) so you can explore the dashboard without setting up a real wedding.</>
                }
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            {!demoWedding ? (
              <Button
                onClick={handleCreateDemo}
                disabled={demoBusy === 'create'}
                className="bg-terracotta text-white hover:bg-terracotta-dark gap-2"
                data-testid="admin-create-demo-wedding-btn"
              >
                <Sparks className="w-4 h-4" strokeWidth={2} />
                {demoBusy === 'create' ? 'Creating…' : 'Create demo wedding'}
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleOpenDemo}
                  className="bg-terracotta text-white hover:bg-terracotta-dark gap-2"
                  data-testid="admin-open-demo-wedding-btn"
                >
                  <OpenInBrowser className="w-4 h-4" strokeWidth={2} />
                  Open in dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDeleteDemo(true)}
                  disabled={demoBusy === 'delete'}
                  className="gap-2 border-border text-charcoal hover:bg-page"
                  data-testid="admin-delete-demo-wedding-btn"
                >
                  <Trash className="w-4 h-4" strokeWidth={1.75} />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Dialog open={confirmDeleteDemo} onOpenChange={(o) => !o && setConfirmDeleteDemo(false)}>
        <DialogContent data-testid="admin-delete-demo-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete demo wedding?</DialogTitle>
            <DialogDescription>
              This removes the demo wedding and all of its sample events, checklist, vendors, expenses, guests, and seating. Real user data is not touched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteDemo(false)} disabled={demoBusy === 'delete'}>Cancel</Button>
            <Button
              onClick={handleDeleteDemo}
              disabled={demoBusy === 'delete'}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="admin-delete-demo-confirm-btn"
            >
              {demoBusy === 'delete' ? 'Deleting…' : 'Delete demo wedding'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending invites panel */}
      <PendingInvitesCard refreshKey={invitesRefreshKey} />

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full pl-9 pr-3 py-2.5 border border-border bg-white font-sans text-sm focus:outline-none focus:border-terracotta"
            data-testid="admin-user-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-terracotta"
          data-testid="admin-user-filter"
        >
          <option value="">All users</option>
          <option value="paid">Paid only</option>
          <option value="unpaid">Unpaid only</option>
          <option value="tester">Testers</option>
          <option value="admin">Admins</option>
        </select>
        <span className="text-xs text-muted font-mono self-center whitespace-nowrap">{total} total</span>
        <Button onClick={() => setCreateOpen(true)} className="bg-terracotta text-white hover:bg-terracotta-dark gap-2" data-testid="admin-create-user-btn">
          <Plus className="w-4 h-4" strokeWidth={2} /> New user
        </Button>
      </div>

      <Card className="card-surface overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && users.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted text-sm">Loading…</TableCell></TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted text-sm">No users match</TableCell></TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium text-charcoal">{u.name || '—'}</div>
                  <div className="text-xs text-muted font-mono">{u.email}</div>
                </TableCell>
                <TableCell>
                  {u.role === 'admin' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-mono text-terracotta">
                      <Sparks className="w-3 h-3" strokeWidth={2} /> Admin
                    </span>
                  ) : u.role === 'tester' ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-mono text-ochre"
                      data-testid={`user-tester-badge-${u.id}`}
                    >
                      <Flower className="w-3 h-3" strokeWidth={2} /> Tester
                    </span>
                  ) : u.is_paid ? (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-mono text-moss">
                      <CheckCircle className="w-3 h-3" strokeWidth={2} /> Paid
                      {u.manually_granted && <span className="text-muted">· manual</span>}
                    </span>
                  ) : (
                    <span className="text-[10px] tracking-widest uppercase font-mono text-muted">Unpaid</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    {u.role !== 'admin' && !u.is_paid && (
                      <Button onClick={() => setConfirmAction({ type: 'grant', user: u })} className="btn-secondary text-xs py-1 px-2" data-testid={`grant-${u.id}`}>
                        Grant
                      </Button>
                    )}
                    {u.role !== 'admin' && u.is_paid && (
                      <Button onClick={() => setConfirmAction({ type: 'revoke', user: u })} className="btn-secondary text-xs py-1 px-2" data-testid={`revoke-${u.id}`}>
                        Revoke
                      </Button>
                    )}
                    {u.role !== 'admin' && (
                      <Button onClick={() => setConfirmAction({ type: 'delete', user: u })} className="btn-secondary text-xs py-1 px-2 text-terracotta" data-testid={`delete-${u.id}`}>
                        <Trash className="w-3 h-3" strokeWidth={1.5} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <DialogContent className="sm:max-w-md bg-page" data-testid="admin-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-charcoal font-light">
              {confirmAction?.type === 'grant' && 'Grant paid access?'}
              {confirmAction?.type === 'revoke' && 'Revoke paid access?'}
              {confirmAction?.type === 'delete' && 'Delete this user?'}
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono text-charcoal">{confirmAction?.user?.email}</span>
              <br />
              {confirmAction?.type === 'grant' && "They'll get immediate access to the full app — no payment needed."}
              {confirmAction?.type === 'revoke' && "They'll be blocked from the app and bounced to the paywall."}
              {confirmAction?.type === 'delete' && "This permanently deletes the user and their wedding. It does NOT refund anything — use the Transactions tab to refund first."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button onClick={() => setConfirmAction(null)} className="btn-secondary">Cancel</Button>
            <Button
              onClick={runAction}
              className={confirmAction?.type === 'delete' ? "bg-terracotta text-white hover:bg-terracotta-dark" : "btn-primary"}
              data-testid="admin-confirm-btn"
            >
              {confirmAction?.type === 'grant' && 'Grant access'}
              {confirmAction?.type === 'revoke' && 'Revoke access'}
              {confirmAction?.type === 'delete' && 'Yes, delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create user modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-page border-border max-w-md" data-testid="admin-create-user-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-charcoal font-light">New user</DialogTitle>
            <DialogDescription className="text-muted">
              {createMode === 'invite'
                ? "Send an invite email. The recipient picks their own password."
                : "Create the account now with a temporary password you'll share."}
            </DialogDescription>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-0 border border-border rounded overflow-hidden" data-testid="new-user-mode-toggle">
            <button type="button" onClick={() => setCreateMode('password')}
              data-testid="mode-password"
              className={`py-2.5 text-sm transition-colors ${createMode === 'password' ? 'bg-terracotta text-ivory' : 'bg-white text-charcoal hover:bg-page'}`}>
              Set password now
            </button>
            <button type="button" onClick={() => setCreateMode('invite')}
              data-testid="mode-invite"
              className={`py-2.5 text-sm transition-colors ${createMode === 'invite' ? 'bg-terracotta text-ivory' : 'bg-white text-charcoal hover:bg-page'}`}>
              Send invite email
            </button>
          </div>

          <form onSubmit={createUser} className="space-y-4">
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Email</label>
              <input
                type="email" required value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="user@example.com"
                className="w-full border border-border px-3 py-2 bg-white text-sm focus:outline-none focus:border-terracotta"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Name</label>
              <input
                type="text" required value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Priya Sharma"
                className="w-full border border-border px-3 py-2 bg-white text-sm focus:outline-none focus:border-terracotta"
                data-testid="new-user-name"
              />
            </div>
            {createMode === 'password' && (
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Temporary password (≥8 chars)</label>
                <input
                  type="text" required minLength="8" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Share this with the user"
                  className="w-full border border-border px-3 py-2 bg-white text-sm focus:outline-none focus:border-terracotta font-mono"
                  data-testid="new-user-password"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    // Testers always bypass paywall — force is_paid true
                    setNewUser((prev) => ({ ...prev, role, is_paid: role === 'tester' ? true : prev.is_paid }));
                  }}
                  className="w-full border border-border px-3 py-2 bg-white text-sm focus:outline-none focus:border-terracotta"
                  data-testid="new-user-role"
                >
                  <option value="user">Regular user</option>
                  <option value="tester">Tester</option>
                  <option value="admin">Admin</option>
                </select>
                {newUser.role === 'tester' && (
                  <p className="mt-1 text-[10px] text-ochre font-mono uppercase tracking-wider">
                    Testers bypass the paywall · excluded from revenue metrics
                  </p>
                )}
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Access</label>
                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm text-charcoal">
                  <input type="checkbox" checked={newUser.is_paid}
                    onChange={(e) => setNewUser({ ...newUser, is_paid: e.target.checked })}
                    className="w-4 h-4 accent-terracotta" data-testid="new-user-is-paid" />
                  Mark as paid
                </label>
              </div>
            </div>
            {createMode === 'invite' && (
              <p className="text-[11px] text-muted font-light leading-relaxed" data-testid="invite-mode-hint">
                Requires Resend to be configured. The invite link expires in 7 days. The recipient lands on /accept-invite and sets their own password there.
              </p>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => setCreateOpen(false)} className="btn-secondary">Cancel</Button>
              <Button type="submit" disabled={creating} className="bg-terracotta text-white hover:bg-terracotta-dark" data-testid="new-user-submit">
                {creating ? (createMode === 'invite' ? 'Sending…' : 'Creating…') : (createMode === 'invite' ? 'Send invite' : 'Create user')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────── TRANSACTIONS ─────────────
function TransactionsTab() {
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (statusFilter) params.set('payment_status', statusFilter);
      const { data } = await axios.get(`${API}/admin/transactions?${params}`, { withCredentials: true });
      setTxs(data.transactions);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load transactions');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const runRefund = async () => {
    if (!refundTarget) return;
    setRefunding(true);
    try {
      await axios.post(
        `${API}/admin/transactions/${refundTarget.session_id}/refund`,
        { reason: refundReason },
        { withCredentials: true }
      );
      toast.success('Refund issued — access revoked');
      setRefundTarget(null);
      setRefundReason('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally { setRefunding(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-transactions-tab">
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-terracotta"
          data-testid="admin-tx-filter"
        >
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="initiated">Initiated</option>
          <option value="expired">Expired</option>
        </select>
        <span className="text-xs text-muted font-mono">{total} total</span>
      </div>

      <Card className="card-surface overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Tier</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && txs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted text-sm">Loading…</TableCell></TableRow>
            )}
            {!loading && txs.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted text-sm">No transactions</TableCell></TableRow>
            )}
            {txs.map((t) => (
              <TableRow key={t.session_id} data-testid={`tx-row-${t.session_id}`}>
                <TableCell className="text-xs text-muted">{fmtDate(t.created_at)}</TableCell>
                <TableCell className="text-sm text-charcoal font-mono">{t.user_email || '—'}</TableCell>
                <TableCell className="font-mono text-charcoal">{fmtCurrency(t.amount)}</TableCell>
                <TableCell className="text-xs text-muted uppercase tracking-wider">{t.tier || '—'}</TableCell>
                <TableCell>
                  {t.refunded ? (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-mono text-terracotta">
                      <RefreshDouble className="w-3 h-3" strokeWidth={2} /> Refunded
                    </span>
                  ) : t.payment_status === 'paid' ? (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase font-mono text-moss">
                      <CheckCircle className="w-3 h-3" strokeWidth={2} /> Paid
                    </span>
                  ) : (
                    <span className="text-[10px] tracking-widest uppercase font-mono text-muted">{t.payment_status || 'initiated'}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {t.payment_status === 'paid' && !t.refunded && (
                    <Button onClick={() => setRefundTarget(t)} className="btn-secondary text-xs py-1 px-2" data-testid={`refund-${t.session_id}`}>
                      Refund
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Refund dialog */}
      <Dialog open={!!refundTarget} onOpenChange={(v) => !v && setRefundTarget(null)}>
        <DialogContent className="sm:max-w-md bg-page" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-charcoal font-light flex items-center gap-2">
              <WarningTriangle className="w-5 h-5 text-terracotta" strokeWidth={1.5} /> Issue refund
            </DialogTitle>
            <DialogDescription>
              <span className="font-mono text-charcoal">{refundTarget?.user_email}</span> · <span className="font-mono">{fmtCurrency(refundTarget?.amount)}</span>
              <br />
              Stripe will refund the full amount to the original card. The user's paid access will be revoked immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="block text-xs tracking-widest uppercase font-mono text-muted">Reason (internal, optional)</label>
            <input
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="e.g. Customer request — within 7-day window"
              className="w-full border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-terracotta"
              data-testid="refund-reason-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setRefundTarget(null)} className="btn-secondary" disabled={refunding}>Cancel</Button>
            <Button onClick={runRefund} disabled={refunding} className="bg-terracotta text-white hover:bg-terracotta-dark" data-testid="confirm-refund-btn">
              {refunding ? 'Refunding…' : 'Issue refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────── PAGE ─────────────
// ───────────── SYSTEM STATUS ─────────────
const SERVICE_META = {
  mongodb: { label: 'MongoDB',  subtitle: 'Primary datastore — weddings, users, transactions' },
  stripe:  { label: 'Stripe',   subtitle: 'Payment processing & checkout sessions' },
  resend:  { label: 'Resend',   subtitle: 'Receipt emails & admin alerts' },
};

function StatusDot({ state }) {
  // state: 'ok' | 'warn' | 'down'
  const map = {
    ok:   { bg: 'bg-moss',       ring: 'ring-moss/30',       pulse: true  },
    warn: { bg: 'bg-ochre',      ring: 'ring-ochre/30',      pulse: false },
    down: { bg: 'bg-terracotta', ring: 'ring-terracotta/30', pulse: true  },
  };
  const s = map[state] || map.warn;
  return (
    <span className="relative flex h-3 w-3" data-testid={`status-dot-${state}`}>
      {s.pulse && <span className={`absolute inline-flex h-full w-full rounded-full ${s.bg} opacity-40 animate-ping`} />}
      <span className={`relative inline-flex rounded-full h-3 w-3 ${s.bg} ring-4 ${s.ring}`} />
    </span>
  );
}

function serviceState(svc) {
  if (!svc) return 'down';
  if (svc.ok === false) return 'down';
  if (svc.warning) return 'warn';
  return 'ok';
}

function SystemStatusTab() {
  const [health, setHealth] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/health`, { withCredentials: true, validateStatus: () => true });
      setHealth(r.data);
      setLastChecked(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || 'Network error');
      setLastChecked(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const overall = error ? 'down' : (health?.ok === true ? 'ok' : 'down');

  if (loading && !health) return <p className="text-muted text-sm">Checking system status…</p>;

  return (
    <div className="space-y-6" data-testid="admin-system-tab">
      {/* Overall bar */}
      <Card className={`card-surface p-6 border-l-4 ${overall === 'ok' ? 'border-l-moss' : 'border-l-terracotta'}`} data-testid="system-overall-card">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <StatusDot state={overall} />
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-1">System status</p>
              <p className="font-serif text-2xl text-charcoal font-light" data-testid="system-overall-label">
                {overall === 'ok' ? 'All systems operational' : 'Service degradation detected'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted font-mono" data-testid="system-last-checked">
              {lastChecked ? `Last check: ${lastChecked.toLocaleTimeString()}` : '—'}
            </p>
            <Button onClick={load} className="btn-secondary text-xs gap-2" data-testid="system-refresh-btn">
              <Refresh className="w-3 h-3" strokeWidth={2} /> Refresh
            </Button>
          </div>
        </div>
        {error && (
          <p className="text-sm text-terracotta mt-4" data-testid="system-fetch-error">
            Couldn't reach /api/health: {error}
          </p>
        )}
      </Card>

      {/* Per-service grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {Object.entries(SERVICE_META).map(([key, meta]) => {
          const svc = health?.services?.[key];
          const state = serviceState(svc);
          const mode = svc?.mode;
          const warning = svc?.warning;
          const errMsg = svc?.error;
          const latency = svc?.latency_ms;
          return (
            <Card key={key} className="card-surface p-5" data-testid={`system-service-${key}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-serif text-xl text-charcoal font-light">{meta.label}</p>
                  <p className="text-[11px] text-muted font-light mt-1 leading-snug">{meta.subtitle}</p>
                </div>
                <StatusDot state={state} />
              </div>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border/50">
                <div className="flex-1">
                  <p className="text-[10px] tracking-widest uppercase font-mono text-muted">Latency</p>
                  <p className="font-mono text-sm text-charcoal" data-testid={`system-${key}-latency`}>
                    {typeof latency === 'number' ? `${latency}ms` : '—'}
                  </p>
                </div>
                {mode && (
                  <div className="flex-1">
                    <p className="text-[10px] tracking-widest uppercase font-mono text-muted">Mode</p>
                    <p className="font-mono text-sm text-charcoal" data-testid={`system-${key}-mode`}>{mode}</p>
                  </div>
                )}
                {svc?.configured !== undefined && (
                  <div className="flex-1">
                    <p className="text-[10px] tracking-widest uppercase font-mono text-muted">Configured</p>
                    <p className="font-mono text-sm text-charcoal">{svc.configured ? 'yes' : 'no'}</p>
                  </div>
                )}
              </div>
              {warning && (
                <p className="text-xs text-ochre mt-3 leading-relaxed" data-testid={`system-${key}-warning`}>
                  <WarningTriangle className="inline w-3 h-3 mr-1" strokeWidth={2} /> {warning}
                </p>
              )}
              {errMsg && (
                <p className="text-xs text-terracotta mt-3 leading-relaxed font-mono break-words" data-testid={`system-${key}-error`}>
                  {errMsg}
                </p>
              )}
            </Card>
          );
        })}
      </div>

      <AlertSettingsCard />

      <IncidentTimelineCard />

      <p className="text-[11px] text-muted font-light text-center">
        Auto-refreshing every 30 seconds. Hit <code className="font-mono">/api/health</code> directly for JSON.
      </p>
    </div>
  );
}

// ───────────── INCIDENT TIMELINE (inside System tab) ─────────────
function _formatRelative(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function IncidentTimelineCard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/alerts/events?limit=30`, { withCredentials: true });
      setEvents(data.events || []);
    } catch {
      /* fail silently — not worth toasting repeatedly */
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <Card className="card-surface p-6" data-testid="incident-timeline-card">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-1">Incidents</p>
          <p className="font-serif text-2xl text-charcoal font-light">Alert timeline</p>
          <p className="text-xs text-muted font-light mt-1">Last 30 alerts dispatched by the background poller.</p>
        </div>
        <Button onClick={load} className="btn-secondary text-xs gap-2" data-testid="timeline-refresh-btn">
          <Refresh className="w-3 h-3" strokeWidth={2} /> Refresh
        </Button>
      </div>

      {loading && events.length === 0 ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : events.length === 0 ? (
        <div className="text-center py-10" data-testid="timeline-empty-state">
          <CheckCircle className="w-8 h-8 text-moss mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-charcoal font-light">No alerts fired yet.</p>
          <p className="text-xs text-muted mt-1">Either everything's healthy or alerting is disabled.</p>
        </div>
      ) : (
        <ol className="space-y-3" data-testid="timeline-events-list">
          {events.map((ev) => {
            const services = (ev.triggered || []).map((t) => t.service).join(', ');
            const reasons = (ev.triggered || []).map((t) => t.reason).join(' · ');
            const accent = ev.is_test ? 'border-l-ochre' : 'border-l-terracotta';
            return (
              <li key={ev.id}
                className={`border-l-2 ${accent} pl-4 py-2 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2`}
                data-testid={`timeline-event-${ev.id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-charcoal font-medium">
                    {ev.is_test && <span className="text-[9px] tracking-widest uppercase font-mono text-ochre mr-2">TEST</span>}
                    {services || '—'}
                  </p>
                  <p className="text-xs text-muted font-light mt-1 break-words">{reasons}</p>
                  <p className="text-[10px] text-muted font-mono mt-1">→ {ev.recipient || '(no recipient)'}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-charcoal font-mono" data-testid={`timeline-event-${ev.id}-relative`}>
                    {_formatRelative(ev.sent_at)}
                  </p>
                  <p className="text-[10px] text-muted font-mono">
                    {ev.sent_at ? new Date(ev.sent_at).toLocaleString() : '—'}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

// ───────────── ALERT SETTINGS (inside System tab) ─────────────
function AlertSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/alerts/config`, { withCredentials: true });
      setCfg(data);
    } catch {
      toast.error('Failed to load alert settings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/alerts/config`, {
        enabled: cfg.enabled,
        recipient_email: cfg.recipient_email || '',
        stripe_latency_threshold_ms: Number(cfg.stripe_latency_threshold_ms),
        stripe_consecutive_breaches: Number(cfg.stripe_consecutive_breaches),
        cooldown_minutes: Number(cfg.cooldown_minutes),
        alert_on_mongodb_down: cfg.alert_on_mongodb_down,
        alert_on_resend_error: cfg.alert_on_resend_error,
      }, { withCredentials: true });
      setCfg(data);
      toast.success('Alert settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/admin/alerts/test`, {}, { withCredentials: true });
      toast.success(`Test alert sent to ${data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test alert failed');
    } finally { setTesting(false); }
  };

  if (loading) return <p className="text-muted text-sm">Loading alert settings…</p>;
  if (!cfg) return null;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <Card className="card-surface p-6 space-y-5" data-testid="alert-settings-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-1">Alerts</p>
          <p className="font-serif text-2xl text-charcoal font-light">Paging thresholds</p>
          <p className="text-xs text-muted font-light mt-1">
            Email an admin when a service breaches its limit. Checked every 60s server-side.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="alert-enabled-toggle">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            className="w-4 h-4 accent-terracotta"
          />
          <span className="text-sm text-charcoal">{cfg.enabled ? 'Alerting ON' : 'Alerting OFF'}</span>
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Recipient email</label>
          <input
            type="email"
            value={cfg.recipient_email || ''}
            onChange={(e) => set('recipient_email', e.target.value)}
            placeholder="ops@yourdomain.com"
            className="w-full border border-border px-3 py-2 bg-white font-sans text-sm focus:outline-none focus:border-terracotta transition-colors"
            data-testid="alert-recipient-input"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Cooldown (minutes)</label>
          <input
            type="number" min="1" max="240"
            value={cfg.cooldown_minutes ?? 15}
            onChange={(e) => set('cooldown_minutes', e.target.value)}
            className="w-full border border-border px-3 py-2 bg-white font-mono text-sm focus:outline-none focus:border-terracotta"
            data-testid="alert-cooldown-input"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 pt-2 border-t border-border/50">
        <div>
          <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Stripe latency threshold (ms)</label>
          <input
            type="number" min="50" max="30000" step="50"
            value={cfg.stripe_latency_threshold_ms ?? 500}
            onChange={(e) => set('stripe_latency_threshold_ms', e.target.value)}
            className="w-full border border-border px-3 py-2 bg-white font-mono text-sm focus:outline-none focus:border-terracotta"
            data-testid="alert-stripe-threshold-input"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest uppercase font-mono text-muted mb-2">Stripe consecutive breaches</label>
          <input
            type="number" min="1" max="20"
            value={cfg.stripe_consecutive_breaches ?? 2}
            onChange={(e) => set('stripe_consecutive_breaches', e.target.value)}
            className="w-full border border-border px-3 py-2 bg-white font-mono text-sm focus:outline-none focus:border-terracotta"
            data-testid="alert-stripe-consecutive-input"
          />
        </div>
      </div>

      <div className="flex gap-6 pt-2 border-t border-border/50">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-charcoal">
          <input type="checkbox" checked={!!cfg.alert_on_mongodb_down}
            onChange={(e) => set('alert_on_mongodb_down', e.target.checked)}
            className="w-4 h-4 accent-terracotta" data-testid="alert-mongodb-toggle" />
          Alert when MongoDB goes down
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-charcoal">
          <input type="checkbox" checked={!!cfg.alert_on_resend_error}
            onChange={(e) => set('alert_on_resend_error', e.target.checked)}
            className="w-4 h-4 accent-terracotta" data-testid="alert-resend-toggle" />
          Alert on Resend config error
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={save} disabled={saving}
          className="bg-terracotta text-white hover:bg-terracotta-dark" data-testid="alert-save-btn">
          {saving ? 'Saving…' : 'Save settings'}
        </Button>
        <Button onClick={sendTest} disabled={testing || !cfg.recipient_email}
          className="btn-secondary" data-testid="alert-test-btn">
          {testing ? 'Sending…' : 'Send test alert'}
        </Button>
      </div>
    </Card>
  );
}

// ───────────── CONTENT LIBRARY (inside Content tab) ─────────────
function ContentLibraryTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [cultureFilter, setCultureFilter] = useState('');
  const [expanded, setExpanded] = useState(null); // event name

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/admin/content-library`, { withCredentials: true });
        setData(d);
      } catch {
        toast.error('Failed to load content library');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-muted text-sm">Loading content library…</p>;
  if (!data) return null;

  const allCultures = Array.from(new Set(data.events.flatMap((e) => e.cultures))).sort();
  const events = data.events.filter((e) => {
    if (cultureFilter && !e.cultures.includes(cultureFilter)) return false;
    if (query) {
      const q = query.toLowerCase();
      if (!e.name.toLowerCase().includes(q)
        && !(e.description || '').toLowerCase().includes(q)
        && !(e.checklist || []).some((c) => c.title.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="admin-content-tab">
      {/* Totals */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Events', value: data.totals.events, testid: 'content-total-events' },
          { label: 'Templates', value: data.totals.templates, testid: 'content-total-templates' },
          { label: 'Checklist items', value: data.totals.checklist_items, testid: 'content-total-checklist' },
        ].map((s) => (
          <Card key={s.label} className="card-surface p-5">
            <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-1">{s.label}</p>
            <p className="font-serif text-3xl text-charcoal font-light" data-testid={s.testid}>{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" strokeWidth={1.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search event name, description, or checklist task…"
            className="w-full pl-9 pr-3 py-2.5 border border-border bg-white text-sm focus:outline-none focus:border-terracotta"
            data-testid="content-search"
          />
        </div>
        <select
          value={cultureFilter}
          onChange={(e) => setCultureFilter(e.target.value)}
          className="border border-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-terracotta"
          data-testid="content-culture-filter"
        >
          <option value="">All cultures</option>
          {allCultures.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-muted font-mono self-center whitespace-nowrap" data-testid="content-filter-count">
          {events.length} of {data.totals.events}
        </span>
      </div>

      {/* Events list */}
      <div className="space-y-3" data-testid="content-events-list">
        {events.map((ev) => {
          const isOpen = expanded === ev.name;
          return (
            <Card key={ev.name} className="card-surface overflow-hidden" data-testid={`content-event-${ev.name.replace(/[^a-zA-Z0-9]/g, '-')}`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : ev.name)}
                className="w-full text-left p-5 hover:bg-page/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <p className="font-serif text-xl text-charcoal font-light">{ev.name}</p>
                      {ev.cultures.map((c) => (
                        <span key={c} className="text-[10px] tracking-widest uppercase font-mono text-muted">{c}</span>
                      ))}
                    </div>
                    {ev.description && <p className="text-sm text-muted font-light mt-1">{ev.description}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono text-sm text-charcoal">{ev.checklist.length} tasks</p>
                    {ev.timeline && ev.timeline.length > 0 && (
                      <p className="font-mono text-xs text-muted mt-1">{ev.timeline.length} timeline steps</p>
                    )}
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 border-t border-border/50 pt-4 bg-page/30">
                  <p className="text-[10px] tracking-widest uppercase font-mono text-muted mb-3">Checklist ({ev.checklist.length})</p>
                  <ul className="space-y-1.5">
                    {ev.checklist.map((c, i) => (
                      <li key={`${c.title}-${c.priority}-${i}`} className="flex items-start gap-3 text-sm">
                        <span className={`inline-block mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          c.priority === 'high' ? 'bg-terracotta' : c.priority === 'medium' ? 'bg-ochre' : 'bg-muted'
                        }`} />
                        <span className="flex-1 text-charcoal">{c.title}</span>
                        <span className="text-[10px] text-muted font-mono whitespace-nowrap">{c.category}</span>
                      </li>
                    ))}
                  </ul>
                  {ev.timeline && ev.timeline.length > 0 && (
                    <>
                      <p className="text-[10px] tracking-widest uppercase font-mono text-muted mt-5 mb-3">Suggested timeline</p>
                      <ul className="space-y-1.5">
                        {ev.timeline.map((t, i) => (
                          <li key={`${t.start_time}-${t.title}-${i}`} className="flex items-center gap-3 text-sm">
                            <span className="font-mono text-xs text-muted whitespace-nowrap w-28">{t.start_time} → {t.end_time}</span>
                            <span className="flex-1 text-charcoal">{t.title}</span>
                            <span className="text-[10px] text-muted font-mono">{t.category}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {events.length === 0 && (
          <div className="text-center py-12 text-muted text-sm" data-testid="content-empty-state">
            No events match that search.
          </div>
        )}
      </div>

      {/* Templates summary */}
      <Card className="card-surface p-6" data-testid="content-templates-card">
        <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-muted mb-3">Wedding templates</p>
        <p className="text-xs text-muted font-light mb-4">
          Templates are culture-specific presets offered when a user creates their first wedding. Events from any template can be added to any wedding (fusion mode).
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {data.templates.map((t) => (
            <div key={t.id} className="border border-border/50 p-4" data-testid={`content-template-${t.id}`}>
              <p className="font-serif text-lg text-charcoal font-light">{t.name}</p>
              {t.description && <p className="text-xs text-muted mt-1">{t.description}</p>}
              <div className="flex gap-4 mt-3 text-[11px] font-mono text-muted">
                <span>{t.events.length} events</span>
                <span>{t.checklist_count} tasks</span>
                <span>{t.timeline_event_count} timelines</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user?.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading || user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <p className="text-muted font-light">Loading…</p>
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="admin-page">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-terracotta mb-2">Admin</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal">Command center</h1>
          <p className="text-muted text-sm mt-2">Monitor growth, manage access, issue refunds.</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-page-alt border border-border">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="gap-1.5">
              <BookStack className="w-3 h-3" strokeWidth={2} /> Content
            </TabsTrigger>
            <TabsTrigger value="system" data-testid="tab-system" className="gap-1.5">
              <Activity className="w-3 h-3" strokeWidth={2} /> System
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><Overview /></TabsContent>
          <TabsContent value="users"><UsersTab /></TabsContent>
          <TabsContent value="transactions"><TransactionsTab /></TabsContent>
          <TabsContent value="content"><ContentLibraryTab /></TabsContent>
          <TabsContent value="system"><SystemStatusTab /></TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
